/**
 * **A port of `legacy/frontend/src/screens/ProfileFields.tsx`, whole but for its `:3`.** F-017 T6:
 * the body of the edit sheet «Имя и партия · …», which T10 puts inside `PersonScreen`'s `Sheet`.
 * Nothing calls it before then.
 *
 * Their `:3` imports `VoicePart` from `../api`. It is a server shape, so here it comes from
 * `@contracts/index` (ADR-0011); `app/src/api.ts` imports the type and does not re-export it —
 * `PartSegments.tsx`'s answer at T5. That is the one line that differs. Their `:1-2` and their
 * `:4` to the end are theirs byte for byte, and `scripts/ported-partial.txt` holds both regions.
 *
 * **One comment below no longer describes the code under it**, and it is noted here rather than
 * beside itself so that the region stays byte-identical. Their `:45-46` say an empty voice part
 * is a choice «and getting back to it has to be possible». Since 2026-08-24 neither control can
 * produce `null` — `PartSegments`'s own doc comment says the fifth segment «—» was taken out, and
 * `SexSegments` has no «не указано» — so `null` survives only as the value a field starts from and
 * is handed back untouched (legacy §7.18). The behaviour is ported; the comment is ported as the
 * record, not as a claim.
 */
import { useState } from 'react';

import type { VoicePart } from '@contracts/index';
import PartSegments from './PartSegments';
import SexSegments from './SexSegments';

/**
 * The name and voice part of somebody in the roster, opened for editing by the
 * regent.
 *
 * It is the body of a sheet since 2026-08-21, and the reason is the keyboard.
 * Unfolded inside the roster row it was a form near the middle of a scrolling
 * list, so raising the keyboard took the name field and «Сохранить» under it
 * together; a sheet stands over the screen and rises with the keyboard, which
 * `Sheet` already does through `--kb`. The menu the form was opened from stays
 * unfolded behind it: closing the sheet puts the regent back exactly where they
 * left.
 *
 * The two fields are the regent's records. The birthday and the avatar are not
 * his to set - they are the person's, chosen on «Профиль» - so the roster's copy
 * of a member's own values rides through its `onSave` untouched.
 *
 * There is no «Отмена». The sheet closes on the handle, on the darkening and on
 * the way back, and a fourth way out would only crowd the one button that saves.
 *
 * It was shared with the person's own edit on «Я» until 2026-08-21, when that
 * form and the avatar constructor became one screen and this was all that was
 * left of the other half. What the two still share is the voice part, which is
 * one component rather than three fields that look alike - see PartSegments.
 */
export default function ProfileFields({
  initialName,
  initialVoicePart,
  initialIsBrother,
  busy,
  onSave,
}: {
  initialName: string;
  initialVoicePart: VoicePart | null;
  initialIsBrother: boolean | null;
  busy: boolean;
  onSave: (name: string, voicePart: VoicePart | null, isBrother: boolean | null) => void;
}) {
  const [name, setName] = useState(initialName);
  // An empty voice part is a choice here rather than the absence of one: "not
  // stated" is a legitimate state, and getting back to it has to be possible.
  const [voicePart, setVoicePart] = useState<VoicePart | null>(initialVoicePart);
  const [isBrother, setIsBrother] = useState<boolean | null>(initialIsBrother);

  const trimmed = name.trim();

  return (
    <>
      <label className="field">
        <span>Как записать</span>
        <input
          value={name}
          maxLength={100}
          placeholder="Как записать"
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <div className="field">
        <span>Партия</span>
        <PartSegments value={voicePart} busy={busy} onPick={setVoicePart} />
      </div>

      {/* The regent's copy carries it too, and that is where it will actually
          get set: filling in a new member's row is the one moment anybody
          passes through, and a field only reachable from somebody else's own
          «Профиль» is a field the choir half fills in. It is a record about the
          choir rather than a fact about a person's life, which is why it is
          here and the birthday is not. */}
      <div className="field">
        <span>Пол</span>
        <SexSegments value={isBrother} busy={busy} onPick={setIsBrother} />
      </div>

      {/* The last line of the sheet and its one action, the way «Сохранить» is
          on the add-a-gathering sheet. It was outlined while this form opened
          inside a roster row, because the filled accent is the weight of what a
          whole screen is for; a sheet is one. */}
      <button
        className="btn btn--primary"
        disabled={busy || trimmed === ''}
        onClick={() => onSave(trimmed, voicePart, isBrother)}
      >
        Сохранить
      </button>
    </>
  );
}
